import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useVisibleRefresh } from "@/hooks/useVisibleRefresh";
import { toast } from "sonner";
import { useDoxaConfirm } from "@/components/doxa/DoxaConfirmDialog";
import {
  AlertTriangle,
  Bell,
  BellRing,
  CheckCircle2,
  Clock3,
  History,
  Loader2,
  RefreshCw,
  Search,
  Send,
  Smartphone,
  UserPlus,
  Users,
} from "lucide-react";
import { norm } from "@/components/RelationSelects";
import DoxaSelect from "@/components/doxa/DoxaSelect";
import { EmptyState, PageHeader, SectionCard, StatCard } from "@/components/doxa/AdminSurface";
import { PUSH_STATUS_LABEL, DISPATCH_STATUS_LABEL, CATEGORY_LABEL, tr, brDateTime } from "@/lib/labels";
import { enablePush, pushSupported } from "@/lib/push";

const STATUS_BADGE = {
  ACTIVE: "doxa-badge-success",
  NOT_ACTIVATED: "doxa-badge-neutral",
  BLOCKED: "doxa-badge-warning",
  UNSUPPORTED: "doxa-badge-neutral",
  INACTIVE_DEVICE: "doxa-badge-warning",
  FAILED: "doxa-badge-critical",
};

const STATUS_META = {
  ACTIVE: { rail: "bg-[#2F855A]", icon: "bg-[#EAF5EE] text-[#2F7A45]" },
  NOT_ACTIVATED: { rail: "bg-[#A9A39A]", icon: "bg-[#F3EFE7] text-[#6B655E]" },
  BLOCKED: { rail: "bg-[#D98A2B]", icon: "bg-[#FFF2DE] text-[#B96712]" },
  UNSUPPORTED: { rail: "bg-[#A9A39A]", icon: "bg-[#F3EFE7] text-[#6B655E]" },
  INACTIVE_DEVICE: { rail: "bg-[#D98A2B]", icon: "bg-[#FFF2DE] text-[#B96712]" },
  FAILED: { rail: "bg-[#D9534F]", icon: "bg-[#FCE8E7] text-[#B33A36]" },
};

const TAB_OPTIONS = [
  { value: "status", label: "Status por turma", icon: Users },
  { value: "history", label: "Histórico de disparos", icon: History },
];

function RegistrationPushCard() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/push/registration/status").then(({ data }) => setState(data)).catch(() => setState({ eligible: false }));
  useEffect(() => { load(); }, []);
  useVisibleRefresh(() => load(), { intervalMs: 0 });

  if (!state || !state.eligible) return null;

  const enabled = !!state.wants_registration_push;
  const browserBlocked = typeof Notification !== "undefined" && Notification.permission === "denied";

  const toggle = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (!enabled) {
        if (!state.has_active_subscription) {
          if (!pushSupported()) {
            toast.error("Seu navegador não suporta notificações push.");
            return;
          }
          if (browserBlocked) {
            toast.error("As notificações estão bloqueadas no navegador. Habilite-as nas configurações do site e tente novamente.");
            return;
          }
          const res = await enablePush();
          if (!res.ok) {
            if (res.reason === "denied") toast.error("Permissão negada. Habilite as notificações nas configurações do navegador.");
            else if (res.reason === "unsupported") toast.error("Seu navegador não suporta notificações push.");
            else toast.error("Não foi possível ativar as notificações. Tente novamente.");
            return;
          }
        }
        await api.post("/push/preference", { wants_registration_push: true });
        toast.success("Alertas de novas inscrições ativados.");
      } else {
        await api.post("/push/preference", { wants_registration_push: false });
        toast.success("Alertas de novas inscrições desativados.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-[#E0D9CD] bg-white p-4 sm:p-5"
      data-testid="registration-push-card"
    >
      <span className={`absolute inset-y-0 left-0 w-1 ${browserBlocked ? "bg-[#D9534F]" : enabled ? "bg-[#2F855A]" : "bg-[#F75A1B]"}`} aria-hidden="true" />
      <div className="flex flex-col gap-4 pl-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${browserBlocked ? "bg-[#FCE8E7] text-[#B33A36]" : enabled ? "bg-[#EAF5EE] text-[#2F7A45]" : "bg-[#FFF0E9] text-[#F75A1B]"}`}>
            <UserPlus className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-display text-base font-bold text-[#1F1F1F]">Alertas de novas inscrições</p>
              <span className={`doxa-badge ${browserBlocked ? "doxa-badge-critical" : enabled ? "doxa-badge-success" : "doxa-badge-neutral"}`}>
                {browserBlocked ? "Bloqueado no navegador" : enabled ? "Ativado" : "Desativado"}
              </span>
            </div>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[#6B655E]">
              Receba um push administrativo quando houver uma nova inscrição pendente para análise.
            </p>
            {browserBlocked && (
              <p className="mt-2 inline-flex items-start gap-1.5 text-xs leading-relaxed text-[#C53030]" data-testid="registration-push-blocked">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                As notificações estão bloqueadas no navegador. Habilite-as nas configurações do site para receber os alertas.
              </p>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={toggle}
          disabled={saving}
          data-testid="registration-push-toggle"
          aria-pressed={enabled}
          className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30 disabled:cursor-not-allowed disabled:opacity-50 ${enabled ? "border border-[#D7EADB] bg-[#F4FBF6] text-[#2F7A45] hover:border-[#2F855A]/40" : "border border-[#F3C7B7] bg-[#FFF7F2] text-[#D94A12] hover:border-[#F75A1B] hover:bg-[#FFF0E9]"}`}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : enabled ? <CheckCircle2 className="h-4 w-4" /> : <BellRing className="h-4 w-4" />}
          {saving ? "Salvando…" : enabled ? "Ativado" : "Ativar"}
        </button>
      </div>
    </section>
  );
}

export default function Notifications() {
  const confirmAction = useDoxaConfirm();
  const [tab, setTab] = useState("status");
  const [classGroups, setClassGroups] = useState([]);
  const [classFilter, setClassFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dispatches, setDispatches] = useState([]);
  const [busyInvite, setBusyInvite] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadStatus = async () => {
    if (refreshing) return;
    setRefreshing(true);
    const params = classFilter ? { class_group_id: classFilter } : {};
    try {
      const { data } = await api.get("/admin/push/status", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  };

  const loadDispatches = () => api.get("/admin/push/dispatches").then(({ data }) => setDispatches(data)).catch(() => {});

  useEffect(() => { api.get("/class-groups").then(({ data }) => setClassGroups(data)); }, []);
  useEffect(() => { loadStatus(); }, [classFilter]);
  useEffect(() => { if (tab === "history") loadDispatches(); }, [tab]);

  useVisibleRefresh(
    () => (tab === "history" ? loadDispatches() : loadStatus()),
    { intervalMs: 0 },
  );

  const cgMap = Object.fromEntries(classGroups.map((c) => [c.id, c.name]));
  const filtered = rows.filter((r) => {
    if (statusFilter && r.status !== statusFilter) return false;
    if (q && !norm([r.full_name, r.email].join(" ")).includes(norm(q))) return false;
    return true;
  });
  const counts = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  const inviteReminder = (payload, label) => confirmAction({
    title: "Mostrar convite novamente?",
    description: <>O Doxa School marcará <strong className="font-semibold text-[#1F1F1F]">{label}</strong> para ver o convite de notificações novamente ao entrar no portal.</>,
    confirmLabel: "Mostrar convite",
    loadingLabel: "Processando…",
    variant: "default",
    dataTestId: "confirm-notification-invite-reminder",
    onConfirm: async () => {
      setBusyInvite(true);
      try {
        const { data } = await api.post("/admin/push/invite-reminder", payload);
        toast.success(`Convite marcado para ${data.marked} aluno(s). Aparecerá quando entrarem no portal.`);
      } finally {
        setBusyInvite(false);
      }
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const classOptions = [
    { value: "", label: "Todas as turmas" },
    ...classGroups.map((c) => ({ value: c.id, label: c.name })),
  ];
  const statusOptions = [
    { value: "", label: "Todos os status" },
    ...Object.entries(PUSH_STATUS_LABEL).map(([value, label]) => ({ value, label })),
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6 lg:p-8" data-testid="notifications-page">
      <PageHeader
        eyebrow="Comunicação"
        title="Notificações"
        description="Acompanhe a adesão ao push, reforce o convite quando necessário e consulte a entrega dos disparos criados em Avisos."
        meta={tab === "status" ? `${rows.length} aluno(s) na visão atual` : `${dispatches.length} disparo(s) no histórico`}
      />

      <RegistrationPushCard />

      <section className="rounded-2xl border border-[#E0D9CD] bg-white p-3 sm:p-4" aria-label="Visões de notificações">
        <div className="inline-flex w-full rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] p-1 sm:w-auto">
          {TAB_OPTIONS.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={`inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition sm:flex-none ${tab === value ? "bg-white text-[#D94A12] shadow-sm" : "text-[#6B655E] hover:text-[#1F1F1F]"}`}
              data-testid={`notif-tab-${value}`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </section>

      {tab === "status" && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard
              title="Push ativo"
              value={counts.ACTIVE || 0}
              hint="Alunos com dispositivo ativo para receber notificações."
              icon={BellRing}
              tone="success"
              compact
              testId="notif-stat-active"
            />
            <StatCard
              title="Não ativaram"
              value={counts.NOT_ACTIVATED || 0}
              hint="Ainda podem receber o convite novamente no portal."
              icon={Bell}
              tone="neutral"
              compact
              testId="notif-stat-not-activated"
            />
            <StatCard
              title="Bloqueados"
              value={counts.BLOCKED || 0}
              hint="A permissão foi bloqueada no navegador ou dispositivo."
              icon={AlertTriangle}
              tone="warning"
              compact
              testId="notif-stat-blocked"
            />
          </div>

          <SectionCard
            title="Filtrar alunos"
            description="Use turma, status e busca para localizar rapidamente quem precisa de atenção."
            icon={Search}
            compact
          >
            <div className="grid gap-3 lg:grid-cols-[minmax(180px,0.9fr)_minmax(180px,0.8fr)_minmax(240px,1.4fr)_auto_auto] lg:items-end">
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.1em] text-[#8A8378]">Turma</label>
                <DoxaSelect
                  value={classFilter}
                  onValueChange={setClassFilter}
                  options={classOptions}
                  ariaLabel="Filtrar notificações por turma"
                  testId="notif-class-filter"
                  className="w-full"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.1em] text-[#8A8378]">Status</label>
                <DoxaSelect
                  value={statusFilter}
                  onValueChange={setStatusFilter}
                  options={statusOptions}
                  ariaLabel="Filtrar notificações por status"
                  testId="notif-status-filter"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="notif-search-input" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.1em] text-[#8A8378]">Busca</label>
                <div className="flex h-10 min-w-0 items-center gap-2 rounded-xl border border-[#E0D9CD] bg-white px-3 transition focus-within:border-[#F75A1B] focus-within:ring-2 focus-within:ring-[#F75A1B]/15">
                  <Search className="h-4 w-4 shrink-0 text-[#8A8378]" />
                  <input
                    id="notif-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar por nome ou e-mail…"
                    className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[#A59C90]"
                    data-testid="notif-search"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={loadStatus}
                disabled={refreshing}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] px-3 text-sm font-semibold text-[#4A4640] transition hover:border-[#D4C9BA] hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="notif-refresh"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Atualizando…" : "Atualizar"}
              </button>

              <button
                type="button"
                onClick={() => inviteReminder(classFilter ? { class_group_id: classFilter } : {}, classFilter ? "a turma" : "todos os alunos")}
                disabled={busyInvite}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-[#F3C7B7] bg-[#FFF7F2] px-3 text-sm font-semibold text-[#D94A12] transition hover:border-[#F75A1B] hover:bg-[#FFF0E9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="notif-invite-all"
              >
                {busyInvite ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
                {busyInvite ? "Processando…" : "Mostrar convite novamente"}
              </button>
            </div>
          </SectionCard>

          <section className="space-y-3" aria-label="Status de notificações dos alunos">
            {filtered.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.NOT_ACTIVATED;
              return (
                <article
                  key={r.student_id}
                  className="relative overflow-hidden rounded-2xl border border-[#E0D9CD] bg-white p-4 transition hover:border-[#D6CBBB] hover:shadow-[0_10px_30px_rgba(68,53,35,0.05)] sm:p-5"
                  data-testid={`notif-row-${r.student_id}`}
                >
                  <span className={`absolute inset-y-0 left-0 w-1 ${meta.rail}`} aria-hidden="true" />
                  <div className="flex flex-col gap-4 pl-1 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${meta.icon}`}>
                        <Smartphone className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="font-display text-base font-bold text-[#1F1F1F]">{r.full_name}</h2>
                          <span className={`doxa-badge ${STATUS_BADGE[r.status]}`} data-testid={`notif-status-${r.student_id}`}>
                            {tr(PUSH_STATUS_LABEL, r.status)}
                          </span>
                        </div>
                        <p className="mt-1 truncate text-sm text-[#6B655E]">{r.email || "Sem e-mail cadastrado"}</p>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6B655E]">
                          <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-[#8A8378]" /> {cgMap[r.class_group_id] || "Sem turma"}</span>
                          <span className="inline-flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5 text-[#8A8378]" /> {r.active_devices} dispositivo(s) ativo(s)</span>
                          <span className="inline-flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5 text-[#8A8378]" /> Última atividade: {brDateTime(r.last_seen_at)}</span>
                        </div>
                      </div>
                    </div>

                    {r.status !== "ACTIVE" && (
                      <button
                        type="button"
                        onClick={() => inviteReminder({ student_id: r.student_id }, r.full_name)}
                        className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-xl border border-[#F3C7B7] bg-[#FFF7F2] px-3 py-2 text-xs font-semibold text-[#D94A12] transition hover:border-[#F75A1B] hover:bg-[#FFF0E9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25 sm:self-center"
                        data-testid={`notif-invite-${r.student_id}`}
                      >
                        <BellRing className="h-3.5 w-3.5" /> Mostrar convite
                      </button>
                    )}
                  </div>
                </article>
              );
            })}

            {filtered.length === 0 && (
              <div className="rounded-2xl border border-[#E0D9CD] bg-white">
                <EmptyState
                  icon={Bell}
                  title="Nenhum aluno encontrado"
                  description="Ajuste os filtros ou a busca para consultar outro grupo de alunos."
                />
              </div>
            )}
          </section>
        </>
      )}

      {tab === "history" && (
        <section className="space-y-3" data-testid="dispatch-history" aria-label="Histórico de disparos push">
          {dispatches.map((d) => {
            const sent = d.status === "SENT";
            const failed = d.status === "FAILED";
            return (
              <article
                key={d.id}
                className="relative overflow-hidden rounded-2xl border border-[#E0D9CD] bg-white p-4 sm:p-5"
                data-testid={`dispatch-${d.id}`}
              >
                <span className={`absolute inset-y-0 left-0 w-1 ${sent ? "bg-[#2F855A]" : failed ? "bg-[#D9534F]" : "bg-[#D98A2B]"}`} aria-hidden="true" />
                <div className="pl-1">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${sent ? "bg-[#EAF5EE] text-[#2F7A45]" : failed ? "bg-[#FCE8E7] text-[#B33A36]" : "bg-[#FFF2DE] text-[#B96712]"}`}>
                        <Send className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">{tr(CATEGORY_LABEL, d.category_snapshot)}</p>
                        <h2 className="mt-0.5 font-display text-lg font-bold leading-tight text-[#1F1F1F]">{d.title_snapshot}</h2>
                      </div>
                    </div>
                    <span className={`doxa-badge shrink-0 ${sent ? "doxa-badge-success" : failed ? "doxa-badge-critical" : "doxa-badge-warning"}`}>
                      {tr(DISPATCH_STATUS_LABEL, d.status)}
                    </span>
                  </div>

                  <p className="mt-3 max-w-4xl whitespace-pre-wrap text-sm leading-relaxed text-[#514A42]">{d.message_snapshot}</p>

                  <div className="mt-4 grid grid-cols-2 gap-2 border-t border-[#EFE7DC] pt-4 sm:grid-cols-4">
                    <div className="rounded-xl bg-[#FAF7F1] px-3 py-2.5">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#8A8378]">Alvos</p>
                      <p className="mt-1 font-display text-lg font-bold text-[#1F1F1F]">{d.total_targets}</p>
                    </div>
                    <div className="rounded-xl bg-[#F4FBF6] px-3 py-2.5">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#5F8D6B]">Enviados</p>
                      <p className="mt-1 font-display text-lg font-bold text-[#2F7A45]">{d.total_sent}</p>
                    </div>
                    <div className="rounded-xl bg-[#FFF7F2] px-3 py-2.5">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#B96712]">Sem app</p>
                      <p className="mt-1 font-display text-lg font-bold text-[#9A5B1A]">{d.total_without_subscription}</p>
                    </div>
                    <div className="rounded-xl bg-[#FFF4F3] px-3 py-2.5">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#B45B58]">Falhas</p>
                      <p className="mt-1 font-display text-lg font-bold text-[#B33A36]">{d.total_failed}</p>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#6B655E]">
                    <span>Por <strong className="font-semibold text-[#4A4640]">{d.sent_by_name || "—"}</strong></span>
                    <span className="inline-flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5 text-[#8A8378]" /> {brDateTime(d.sent_at)}</span>
                  </div>
                </div>
              </article>
            );
          })}

          {dispatches.length === 0 && (
            <div className="rounded-2xl border border-[#E0D9CD] bg-white">
              <EmptyState
                icon={Send}
                title="Nenhum disparo de push ainda"
                description="Os envios realizados a partir de Avisos aparecerão aqui com os resultados de entrega."
              />
            </div>
          )}
        </section>
      )}
    </div>
  );
}
